import { X, User, Mail, Phone, MapPin, Calendar, Shield, Briefcase, Hash, UserCheck } from 'lucide-react';
import { User as UserType } from '@/types/user';
import { Button } from '@/components/ui';

interface UserDetailProps {
  user: UserType | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: () => void;
}

export const UserDetail: React.FC<UserDetailProps> = ({
  user,
  isOpen,
  onClose,
  onEdit,
}) => {
  if (!isOpen || !user) return null;

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'Not specified';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-rgf-gray-200">
          <div className="flex items-center">
            <div className="h-12 w-12 bg-rgf-primary-100 rounded-full flex items-center justify-center mr-4">
              <span className="text-lg font-medium text-rgf-primary-700">
                {user.first_name?.[0]}{user.last_name?.[0]}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-rgf-gray-900">
                {user.first_name} {user.middle_name} {user.last_name}
              </h2>
              <p className="text-sm text-rgf-gray-600">
                Employee ID: {user.employee_id}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {onEdit && (
              <Button
                variant="secondary"
                size="sm"
                onClick={onEdit}
              >
                Edit User
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              icon={<X className="h-5 w-5" />}
              onClick={onClose}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status Badge */}
          <div className="flex items-center justify-between">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              user.status === 'Active' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              <UserCheck className="h-4 w-4 mr-1" />
              {user.status}
            </span>
            <span className="text-sm text-rgf-gray-500">
              Joined {formatDate(user.joining_date)}
            </span>
          </div>

          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-medium text-rgf-gray-900 mb-4 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-rgf-gray-700">First Name</p>
                <p className="text-sm text-rgf-gray-900">{user.first_name || 'Not specified'}</p>
              </div>
              {user.middle_name && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-rgf-gray-700">Middle Name</p>
                  <p className="text-sm text-rgf-gray-900">{user.middle_name}</p>
                </div>
              )}
              <div className="space-y-2">
                <p className="text-sm font-medium text-rgf-gray-700">Last Name</p>
                <p className="text-sm text-rgf-gray-900">{user.last_name || 'Not specified'}</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-rgf-gray-700">Employee ID</p>
                <p className="text-sm text-rgf-gray-900 flex items-center">
                  <Hash className="h-4 w-4 mr-1 text-rgf-gray-500" />
                  {user.employee_id || 'Not specified'}
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-rgf-gray-700">Gender</p>
                <p className="text-sm text-rgf-gray-900">{user.gender || 'Not specified'}</p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-medium text-rgf-gray-900 mb-4 flex items-center">
              <Mail className="h-5 w-5 mr-2" />
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-rgf-gray-700">Email Address</p>
                <p className="text-sm text-rgf-gray-900 flex items-center">
                  <Mail className="h-4 w-4 mr-1 text-rgf-gray-500" />
                  {user.email || 'Not specified'}
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-rgf-gray-700">Phone Number</p>
                <p className="text-sm text-rgf-gray-900 flex items-center">
                  <Phone className="h-4 w-4 mr-1 text-rgf-gray-500" />
                  {user.phone_number || 'Not specified'}
                </p>
              </div>
              <div className="space-y-2 md:col-span-2">
                <p className="text-sm font-medium text-rgf-gray-700">Permanent Address</p>
                <p className="text-sm text-rgf-gray-900 flex items-start">
                  <MapPin className="h-4 w-4 mr-1 text-rgf-gray-500 mt-0.5" />
                  {user.permanent_address || 'Not specified'}
                </p>
              </div>
            </div>
          </div>

          {/* Work Information */}
          <div>
            <h3 className="text-lg font-medium text-rgf-gray-900 mb-4 flex items-center">
              <Briefcase className="h-5 w-5 mr-2" />
              Work Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {user.designation && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-rgf-gray-700">Designation</p>
                  <p className="text-sm text-rgf-gray-900">{user.designation}</p>
                </div>
              )}
              <div className="space-y-2">
                <p className="text-sm font-medium text-rgf-gray-700">District</p>
                <p className="text-sm text-rgf-gray-900 flex items-center">
                  <MapPin className="h-4 w-4 mr-1 text-rgf-gray-500" />
                  {user.district || 'Not specified'}
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-rgf-gray-700">Joining Date</p>
                <p className="text-sm text-rgf-gray-900 flex items-center">
                  <Calendar className="h-4 w-4 mr-1 text-rgf-gray-500" />
                  {formatDate(user.joining_date)}
                </p>
              </div>
            </div>
          </div>

          {/* Roles & Permissions */}
          <div>
            <h3 className="text-lg font-medium text-rgf-gray-900 mb-4 flex items-center">
              <Shield className="h-5 w-5 mr-2" />
              Roles & Permissions
            </h3>
            <div className="flex flex-wrap gap-2">
              {user.roles && user.roles.length > 0 ? (
                user.roles.map((role, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {role}
                  </span>
                ))
              ) : (
                <p className="text-sm text-rgf-gray-500">No roles assigned</p>
              )}
            </div>
          </div>

          {/* System Information */}
          <div>
            <h3 className="text-lg font-medium text-rgf-gray-900 mb-4">System Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-rgf-gray-700">Database ID</p>
                <p className="text-sm text-rgf-gray-900 font-mono">{user.db_id || 'Not available'}</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-rgf-gray-700">FusionAuth User ID</p>
                <p className="text-sm text-rgf-gray-900 font-mono">{user.fusion_auth_user_id || 'Not available'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};